import React, { useCallback, useEffect, useState } from "react";
import { Star, MessageSquare, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { prettyDate } from "@/lib/dates";

export default function ReviewsManager() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/reviews");
      setReviews(res.data);
    } catch (e) {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const remove = async (id) => {
    try {
      await api.delete(`/admin/reviews/${id}`);
      setReviews((prev) => prev.filter((r) => r.id !== id));
      toast.success("Avaliação removida.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="mt-8" data-testid="reviews-manager">
      <div className="flex items-center gap-2">
        <MessageSquare className="h-5 w-5 text-[#1B7A45]" />
        <h2 className="font-display text-xl font-bold tracking-tight text-slate-900">
          Avaliações dos clientes
        </h2>
      </div>
      <p className="mt-1 text-sm text-slate-500">
        Apague comentários indevidos. As avaliações aparecem na página inicial.
      </p>

      <div className="mt-4 space-y-3">
        {loading ? (
          <div className="h-24 animate-pulse rounded-2xl bg-white" />
        ) : reviews.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white py-10 text-center text-sm text-slate-400">
            Nenhuma avaliação ainda.
          </div>
        ) : (
          reviews.map((r) => (
            <div
              key={r.id}
              data-testid={`admin-review-${r.id}`}
              className="flex items-start justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-4"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-display text-base font-bold text-slate-900">{r.name}</span>
                  <span className="flex gap-0.5">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <Star
                        key={n}
                        className={`h-3.5 w-3.5 ${
                          n <= r.rating ? "fill-[#E8611A] text-[#E8611A]" : "fill-transparent text-slate-300"
                        }`}
                      />
                    ))}
                  </span>
                </div>
                <p className="mt-1 text-sm text-slate-700">{r.comment}</p>
                <p className="mt-1 text-xs text-slate-400">
                  {r.created_at ? prettyDate(r.created_at.slice(0, 10)) : ""}
                </p>
              </div>
              <Button
                data-testid={`delete-review-${r.id}`}
                onClick={() => remove(r.id)}
                size="sm"
                variant="outline"
                className="shrink-0 rounded-full border-red-200 bg-red-50 text-red-600 hover:bg-red-100"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
